import type { FaceBox, HeadMeasure } from '@pz/scene';
import type { ImageUploadInfo } from '../../../packages/imaging/src/upload-policy.js';
import { uploadRequest, type UploadProgress } from '../../../packages/imaging/src/upload-transport.js';
import type { ArtworkItem, ArtworkLibrary } from './designer-types.js';
import { localizeDefaults } from './shop-words.js';
import { COLLAGE_TEXTS } from './collage/texts.js';
import type { AudioSelection, ProductDefinition, Values } from './types.js';

/** What an upload answers: the asset, and for a picture its upright size and notices. */
export type UploadResult = { id: string; kind: string; url: string; storageKey: string; previewUrl?: string } & Partial<ImageUploadInfo>;

/**
 * The extras of a `purpose=collage` upload (docs/photo-collage.md §6.1). A
 * URL is null when the server could not make that copy (the widget then
 * draws the preview copy); `faces` is `[]` when none were found and null when
 * detection was unavailable.
 */
export interface CollageUploadBlock {
  /** Long side ≤ 1024, upright: what the page renders the collage with. */
  drawUrl: string | null;
  /** Long side ≤ 256: the tray. */
  thumbUrl: string | null;
  /** Upright size of the original. */
  width?: number;
  height?: number;
  faces: FaceBox[] | null;
}

export type CollageUploadResult = UploadResult & { collage?: CollageUploadBlock };

/**
 * Why an upload failed, for callers that retry by themselves (the collage
 * intake): `busy` — the server asked to wait (429, `conversion_busy`);
 * `network` — no answer, a timeout or a proxy error; `final` — an answer
 * about the file itself (413, 415), which sending again cannot change;
 * `other` — any other refusal.
 */
export type UploadErrorKind = 'busy' | 'network' | 'final' | 'other';

export class UploadError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly kind: UploadErrorKind;
  constructor(message: string, meta: { status?: number; code?: string; kind: UploadErrorKind }) {
    super(message);
    this.name = 'UploadError';
    this.kind = meta.kind;
    if (meta.status !== undefined) this.status = meta.status;
    if (meta.code !== undefined) this.code = meta.code;
  }
}

/** One YouTube search result, as the sound card lists it. */
export interface YoutubeVideo {
  id: string;
  title: string;
  channel?: string;
  durationMs: number;
  thumbnail: string;
  webpageUrl: string;
}

/** The Spotify song found on YouTube (`/v1/storefront/youtube/match`). */
export interface YoutubeMatch {
  video?: YoutubeVideo;
  /** `high`: take it without asking; `low`: let the shopper choose. */
  confidence?: 'high' | 'low';
  candidates: YoutubeVideo[];
  /** What was searched, for "none of these" to start from. */
  query?: string;
  /** Set when nothing came back: `no_results`, `rate_limited`, `unavailable`. */
  code?: string;
  /**
   * Asked with `prefetch`, a `high` match only: the shop fetch of `video` the
   * server already started (or found done or on its way). Picking that video
   * prepares the very same fetch; the id is only a fallback to poll when the
   * prepare request itself is refused.
   */
  prepId?: string;
}

/** What the server reports about a song being fetched for the cutter. */
export interface AudioPrepStatus {
  /** `queued`, `working`, `done` or `error`. */
  status: string;
  progress: number;
  stage: string;
  /** A hint for the shopper on `error`; never the server's own reason. */
  error: string | null;
  /**
   * On `error` only: `locked` when the post itself is closed to every download
   * (private, removed, refused everywhere — no retry will help), `failed` for
   * anything else. Null otherwise, and missing from older servers.
   */
  code?: 'locked' | 'failed' | null;
  /** The song's name and length arrive while it is still `working`, before the file. */
  title: string | null;
  sourceUrl: string | null;
  storageKey: string | null;
  url: string | null;
  durationMs: number | null;
  peaks: number[];
}

/** Where this browser's visit id is kept, beside the drafts it already saves. */
const SESSION_KEY = 'pz_session';

/**
 * This browser's visit id.
 *
 * Photos a shopper uploads now belong to the visit that uploaded them, so the
 * paid work on them — cutting a background out, running an AI style — can be
 * refused to anybody else holding the id. It is not a credential and it is not
 * pretending to be one; it is the same knowability model as the draft this
 * browser already keeps, and it is what stops an upload id being a free,
 * permanent handle on the shop's provider balance.
 *
 * Made once and kept: a shopper who returns tomorrow to finish a design must
 * still own the pictures they left in it.
 */
/**
 * A random id, on browsers that have no `crypto.randomUUID`.
 *
 * That method is both recent (Safari 15.4, Chrome 92) and secure-context only,
 * and shops do exist on plain HTTP and on three-year-old phones. The id is a
 * visit handle, not a secret, so `getRandomValues` — and, failing even that,
 * `Math.random` — is enough to keep uploads owned rather than throwing on the
 * way to the first photo.
 */
function randomId(): string {
  const webcrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (webcrypto && typeof webcrypto.randomUUID === 'function') {
    try {
      return webcrypto.randomUUID();
    } catch {
      // Falls through to the bytes below.
    }
  }
  if (webcrypto && typeof webcrypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    webcrypto.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < bytes.length; i++) out += (bytes[i]! + 0x100).toString(16).slice(1);
    return out;
  }
  return `pz${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function pzSession(): string {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing && /^[A-Za-z0-9_-]{8,64}$/.test(existing)) return existing;
    const made = randomId();
    localStorage.setItem(SESSION_KEY, made);
    return made;
  } catch {
    // Private browsing, or storage the shop's cookie banner has blocked. A
    // per-page id still identifies this visit to the server for as long as it
    // lasts, which is all the ownership check needs.
    return (sessionMemory ??= randomId());
  }
}
let sessionMemory: string | undefined;

/**
 * The sentence to show when the shop's daily allowance for an effect is gone.
 *
 * A budget refusal is not a fault the shopper can fix and not an error they
 * should see in red: the effect is optional, their photo is fine, and the
 * design still works — so it is surfaced as information and the flow carries on
 * without it. Anything else that failed stays silent, exactly as before.
 */
async function budgetNotice(response: Response): Promise<{ notice?: string }> {
  if (response.status !== 429) return {};
  const body = (await response.json().catch(() => ({}))) as { error?: string; code?: string };
  return body.code === 'budget' && body.error ? { notice: body.error } : {};
}

/**
 * Sends a request, again after a pause when the network (not the server's
 * answer) failed: up to four tries over about twenty seconds, waiting for the
 * phone to come back online in between. Server errors a retry can cure (a
 * proxy's 502/504, a timeout, "too many at once") are retried too; answers
 * about the file itself are returned at once. Undefined when every try failed
 * to get any answer.
 */
export async function sendWithRetry(
  attempt: () => Promise<Response>,
  pauses: number[] = [0, 1500, 4000, 9000],
): Promise<Response | undefined> {
  let last: Response | undefined;
  for (const pause of pauses) {
    if (pause > 0) {
      await new Promise((resolve) => setTimeout(resolve, pause));
      await backOnline();
    }
    try {
      last = await attempt();
    } catch {
      last = undefined;
      continue;
    }
    if (last.status === 408 || last.status === 429 || last.status === 500 || last.status === 502 || last.status === 504) continue;
    return last;
  }
  return last;
}

/** Waits (at most 20 s) for a phone that went offline to come back. */
function backOnline(): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine !== false || typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    const done = (): void => {
      window.removeEventListener('online', done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, 20_000);
    window.addEventListener('online', done);
  });
}

/** A commit the shopper cannot fix by themselves. */
const COMMIT_FAILED = 'Personalizacija nije sačuvana. Proveri odgovore i pokušaj ponovo.';

/**
 * The server's reason for refusing one answer, in the shopper's words.
 *
 * The API keeps its reasons in English for logs and scripts
 * (services/validate-answers.ts); an unknown one falls back to a general
 * sentence rather than English on a Serbian page.
 */
export function commitReason(reason: string): string {
  const limit = /^at most (\d+) characters$/.exec(reason);
  if (limit) return `najviše ${limit[1]} znakova`;
  const known: Record<string, string> = {
    'this field takes text': 'ovde se upisuje tekst',
    'this field takes a single choice': 'izaberi jednu opciju',
    'that choice is not offered on this product': 'ta opcija nije dostupna za ovaj proizvod',
    'this field is a yes/no answer': 'odgovor je da ili ne',
    'that is not a date': 'datum nije ispravan',
    'that is not a colour': 'boja nije ispravna',
    'this field takes one picture': 'ovde ide jedna slika',
    'no picture was uploaded': 'slika nije otpremljena — pokušaj ponovo',
    'that picture does not belong to this shop': 'slika nije otpremljena — pokušaj ponovo',
    'that design cannot be read': 'dizajn nije moguće pročitati — otvori ga ponovo',
    'this field takes the chosen sound, not a list': 'izaberi jedan zvuk',
    'part of the design is outside the print area': 'deo dizajna je van oblasti za štampu',
  };
  return known[reason] ?? 'proveri ovaj odgovor';
}

/**
 * A photo collage's refusal (docs/photo-collage.md §6.1), by its code — the
 * English reason is for logs. The step normally stops these before the
 * cart; this is what a shopper reads when a collage slips through anyway.
 */
export function collageCommitReason(code: string | undefined, detail?: string): string | undefined {
  switch (code) {
    case 'collage-incomplete': {
      const missing = Number(detail);
      if (!Number.isInteger(missing) || missing < 1) return 'neka mesta za slike su još prazna';
      const sentence = COLLAGE_TEXTS.missing(missing).replace(/\.$/, '');
      return sentence.charAt(0).toLowerCase() + sentence.slice(1);
    }
    case 'collage-unreadable':
      return 'raspored slika ne može da se pročita — izaberi slike ponovo';
    case 'collage-bad-photo':
    case 'asset-foreign':
    case 'asset-not-upload':
      return 'neka slika nije otpremljena — pošalji je ponovo';
    case 'collage-photo-twice':
      return 'ista slika je navedena dva puta';
    case 'collage-too-many':
      return 'previše slika za ovaj proizvod — ukloni neku';
    case 'collage-bad-fill':
      return 'na jednom mestu je slika koja nije izabrana';
    case 'collage-repeat':
      return 'ovaj proizvod ne dozvoljava istu sliku dva puta';
    default:
      return undefined;
  }
}

/** Talks to the Personalizy API from the shopper's browser. */
export class Api {
  constructor(readonly baseUrl: string, readonly externalOrderToken?: string, readonly externalRevisionToken?: string) {}

  private url(path: string): string {
    return `${this.baseUrl.replace(/\/+$/, '')}${path}`;
  }

  /**
   * Every storefront request, carrying the visit id.
   *
   * One place, because a route that gets the header and a route that does not
   * is how an upload ends up owned by nobody and a cut-out on it is refused.
   */
  private send(url: string, init: RequestInit = {}): Promise<Response> {
    return fetch(url, {
      ...init,
      headers: { ...(init.headers ?? {}), 'x-pz-session': pzSession() },
    });
  }

  async loadProduct(
    wooProductId: number,
    configured?: {
      templateId?: string;
      optionSetId?: string;
      /** A value's own template (Customily's "change template" function): drawn instead of the product's. */
      switchTemplateId?: string;
      /** The variation on the page, for products whose template changes with it. */
      variation?: { id: number | null; attributes: Record<string, string> };
    },
  ): Promise<ProductDefinition> {
    if(this.externalOrderToken){
      const template=configured?.switchTemplateId??configured?.templateId;
      const response=await this.send(this.url(`/v1/external/customer/definition${template?`?templateId=${encodeURIComponent(template)}`:''}`),{headers:{'x-pz-order-link':this.externalOrderToken,accept:'application/json'},cache:'no-store'});
      if(!response.ok){const failure=await response.json().catch(()=>({})) as {error?:string};throw new Error(failure.error??'Sačuvani obrazac nije dostupan. Kontaktirajte BearBox.');}
      return localizeDefaults(await response.json() as ProductDefinition);
    }
    // A product definition is deliberately fetched under a unique URL. Template
    // scenes are edited while the Woo product stays connected, and browser/CDN
    // caches must never keep showing yesterday's scene under the same product id.
    const query = new URLSearchParams({ _pz: String(Date.now()) });
    if (configured?.templateId) query.set('templateId', configured.templateId);
    if (configured?.switchTemplateId) query.set('switchTemplateId', configured.switchTemplateId);
    if (configured?.optionSetId) query.set('optionSetId', configured.optionSetId);
    if (configured?.variation) {
      if (configured.variation.id != null) query.set('variation', String(configured.variation.id));
      query.set('attributes', JSON.stringify(configured.variation.attributes));
    }
    const response = await this.send(this.url(`/v1/storefront/products/${wooProductId}?${query}`), {
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) {
      const detail = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(
        detail.error ?? (response.status === 404
          ? 'This product is not set up for personalization yet.'
          : `Could not load the designer (${response.status}).`),
      );
    }
    return localizeDefaults((await response.json()) as ProductDefinition);
  }

  /**
   * The same definition, for a template that has no product yet.
   *
   * Studio-only and session-authenticated: the harness page is served from the
   * API's own origin, so the cookie rides along and an unpublished template
   * stays private. The payload is built by the same function the shop's route
   * uses, which is what makes the preview worth trusting.
   */
  async loadPreview(preview: {
    templateId: string;
    optionSetId?: string;
    mode?: string;
    previewStyle?: string;
  }): Promise<ProductDefinition> {
    const query = new URLSearchParams({ _pz: String(Date.now()) });
    if (preview.optionSetId) query.set('optionSetId', preview.optionSetId);
    if (preview.mode) query.set('mode', preview.mode);
    if (preview.previewStyle) query.set('previewStyle', preview.previewStyle);
    const response = await this.send(
      this.url(`/v1/templates/${encodeURIComponent(preview.templateId)}/definition?${query}`),
      { headers: { accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' },
    );
    if (!response.ok) {
      const detail = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(
        detail.error ??
          (response.status === 401
            ? 'Sign in to the studio to preview this template.'
            : `Could not load the preview (${response.status}).`),
      );
    }
    return localizeDefaults((await response.json()) as ProductDefinition);
  }

  /**
   * Uploads a shopper's photo or recording.
   *
   * `purpose: 'collage'` (docs/photo-collage.md §6.1) is accepted exactly as a
   * picture is and answers with the `collage` block: the 1024 px draw copy,
   * the 256 px thumbnail, the upright size and the faces the free local
   * detector found. It is sent ONCE: the collage intake retries by itself
   * (a busy server and a lost connection are waited out differently, see
   * collage/intake.ts), so every refusal is thrown as an `UploadError` whose
   * `status` / `code` / `kind` say which of the two it was.
   */
  upload(file: Blob, filename: string, purpose: 'collage', onProgress?: (value: UploadProgress) => void): Promise<CollageUploadResult>;
  upload(file: Blob, filename: string, purpose?: 'image' | 'audio', onProgress?: (value: UploadProgress) => void): Promise<UploadResult>;
  async upload(file: Blob, filename: string, purpose?: 'image' | 'audio' | 'collage', onProgress?: (value: UploadProgress) => void): Promise<UploadResult> {
    const url = this.url(`/v1/storefront/uploads?filename=${encodeURIComponent(filename)}${purpose ? `&purpose=${purpose}` : ''}`);
    // A phone's connection drops, switches from Wi-Fi to mobile data right
    // after the camera, or stalls behind a proxy: the same bytes are sent
    // again (a duplicate is an orphan the nightly sweep removes; a lost photo
    // is a lost sale). Answers the shopper has to act on are not retried.
    // A collage photo is sent once — its intake owns the retries.
    const response = await sendWithRetry(() => {
      const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
      // A slow uplink: 45 s, plus a second for every 50 KB.
      const timer = setTimeout(() => controller?.abort(), 45_000 + Math.round(file.size / 50_000) * 1000);
      return uploadRequest(url, {
        method: 'POST',
        headers: { 'content-type': 'application/octet-stream', 'x-pz-session': pzSession() },
        body: file,
        ...(controller ? { signal: controller.signal } : {}),
      }, onProgress).finally(() => clearTimeout(timer));
    }, purpose === 'collage' ? [0] : undefined);
    if (!response) {
      throw new UploadError('Veza sa internetom je prekinuta pa slika nije poslata. Proveri internet i dodirni „Pokušaj ponovo“ — slika je sačuvana.', { kind: 'network' });
    }
    if (!response.ok) {
      // The server's messages are for logs; what the shopper reads has to be in
      // their language and has to say what to DO, or the sale ends right here.
      if (response.status === 415 || response.status === 503) {
        const body = (await response.json().catch(() => ({}))) as { error?: string; code?: string };
        const messages: Record<string,string> = {
          unsupported_image: 'Ovaj format nije podržan. Izvezi sliku kao JPG ili PNG pa pokušaj ponovo.',
          invalid_image: 'Fajl je oštećen ili ne može da se pročita. Izvezi JPG ili PNG iz aplikacije u kojoj se otvara.',
          conversion_unavailable: 'Pretvaranje ovog formata trenutno nije dostupno. Pošalji JPG ili PNG.',
          conversion_timeout: 'Obrada fajla traje predugo. Pošalji manju sliku ili kraći video.',
          no_video_frame: 'Fajl nema sliku koju možemo da izdvojimo. Pošalji fotografiju ili video sa slikom.',
        };
        // A 503 without a converter's code is a proxy that is down for a
        // moment, not an answer about the file.
        const kind: UploadErrorKind = response.status === 415 || body.code ? 'final' : 'network';
        const meta = { status: response.status, ...(body.code ? { code: body.code } : {}), kind };
        if (body.code && messages[body.code]) throw new UploadError(messages[body.code]!, meta);
        throw new UploadError(
          body.error && /conver|read|page|MB/i.test(body.error)
            ? `Fajl nije mogao da se učita (${body.error}).`
            : 'Ovaj format nije podržan — pošalji JPG, PNG, SVG, PDF ili AI.',
          meta,
        );
      }
      if (response.status === 413) {
        throw new UploadError('Fajl je prevelik: najviše 40 MB i 100 megapiksela za sliku, do 8K za video. Pošalji manju sliku ili kraći video.', { status: 413, kind: 'final' });
      }
      if (response.status === 429) {
        const body = (await response.json().catch(() => ({}))) as { code?: string };
        throw new UploadError('Previše slika odjednom — sačekaj minut pa nastavi.', { status: 429, ...(body.code ? { code: body.code } : {}), kind: 'busy' });
      }
      const detail = (await response.json().catch(() => ({}))) as { error?: string; code?: string };
      const retryable = response.status === 408 || response.status >= 500;
      throw new UploadError(detail.error ?? 'Otpremanje nije uspelo — pokušaj ponovo.', {
        status: response.status,
        ...(detail.code ? { code: detail.code } : {}),
        kind: retryable ? 'network' : 'other',
      });
    }
    return (await response.json()) as UploadResult;
  }

  /**
   * "Upload from your phone": opens a pairing the designer draws as a QR code.
   *
   * The URL is what the phone opens; the token is what this browser polls. Both
   * expire in ten minutes — see the API's `phone-uploads.ts` for why the
   * pictures end up owned by THIS visit rather than by the phone's.
   */
  async pairPhoneUpload(): Promise<{ token: string; url: string; expiresAt: number }> {
    const response = await this.send(this.url('/v1/storefront/uploads/pair'), {
      method: 'POST',
      headers: { accept: 'application/json' },
    });
    if (!response.ok) {
      const detail = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(detail.error ?? 'Veza sa telefonom nije mogla da se otvori.');
    }
    return (await response.json()) as { token: string; url: string; expiresAt: number };
  }

  /**
   * What the phone has sent so far. An expired pairing reads as an empty list
   * rather than as an error: the designer's job is then to offer a new QR code,
   * not to show the shopper a status code.
   */
  async pairedPhotos(token: string): Promise<{
    assets: Array<{ assetId: string; url: string; w: number; h: number }>;
    expired: boolean;
  }> {
    const response = await this.send(
      this.url(`/v1/storefront/uploads/pair/${encodeURIComponent(token)}`),
      { headers: { accept: 'application/json' }, cache: 'no-store' },
    );
    if (response.status === 404) return { assets: [], expired: true };
    if (!response.ok) return { assets: [], expired: false };
    const body = (await response.json().catch(() => ({}))) as {
      assets?: Array<{ assetId: string; url: string; w: number; h: number }>;
    };
    return { assets: body.assets ?? [], expired: false };
  }

  /**
   * The artwork a design area offers: the built-in emoji and icon packs plus
   * whichever of the shop's own collections the zone allows.
   *
   * Failures reach the catalogue panel's retry state. An unavailable service
   * must not pretend the shop has no artwork; other editor tools keep working.
   */
  async artwork(query: {
    /** ProductDefinition.productId (UUID), or a legacy WooCommerce number. */
    productId?: string | number;
    /** The template, in Studio's preview frame (there is no product yet). */
    templateId?: string;
    /** The zone element's id: its own library list decides what is on offer. */
    zone?: string;
    library?: string;
    q?: string;
    offset?: number;
    limit?: number;
  }): Promise<{
    libraries: ArtworkLibrary[];
    items: ArtworkItem[];
    total: number;
    offset: number;
    limit: number;
  }> {
    const empty = { libraries: [], items: [], total: 0, offset: query.offset ?? 0, limit: query.limit ?? 60 };
    const params = new URLSearchParams();
    if (query.productId !== undefined && query.productId !== '') params.set('productId', String(query.productId));
    if (query.templateId) params.set('templateId', query.templateId);
    if (query.zone) params.set('zone', query.zone);
    if (query.library) params.set('library', query.library);
    if (query.q) params.set('q', query.q);
    if (query.offset) params.set('offset', String(query.offset));
    if (query.limit) params.set('limit', String(query.limit));
    const response = await this.send(this.url(`/v1/storefront/artwork?${params}`), {
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Artwork catalogue unavailable (${response.status})`);
    const body = (await response.json()) as Partial<typeof empty>;
    return {
      libraries: Array.isArray(body.libraries) ? body.libraries : [],
      items: Array.isArray(body.items) ? body.items : [],
      total: typeof body.total === 'number' ? body.total : (body.items?.length ?? 0),
      offset: typeof body.offset === 'number' ? body.offset : empty.offset,
      limit: typeof body.limit === 'number' ? body.limit : empty.limit,
    };
  }

  /**
   * Asks the server to restyle a shopper's photo the way the template's layer
   * demands — the AI filter products. The prompt lives on the template; the
   * browser only names the layer.
   *
   * Never throws: a styling that could not be made comes back as a null URL,
   * because the original photo is a worse preview, not a failed sale.
   */
  async stylize(
    templateId: string,
    elementId: string,
    assetId: string,
    /** The shopper's words the layer's prompt reads (`{{el:id}}`), by text layer id. */
    texts: Record<string, string> = {},
    cacheOnly = false,
  ): Promise<{ url: string | null; engine: string; cached: boolean; reason?: string; notice?: string }> {
    const response = await this.send(this.url('/v1/storefront/stylize'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ templateId, elementId, assetId, texts, ...(cacheOnly ? { cacheOnly: true } : {}) }),
    });
    if (!response.ok) return { url: null, engine: 'none', cached: false, ...(await budgetNotice(response)) };
    return (await response.json()) as {
      url: string | null;
      engine: string;
      cached: boolean;
      reason?: string;
    };
  }

  /**
   * Asks the server to cut a shopper's photo out of its background.
   *
   * Never throws for a cut-out that could not be made — that comes back as a
   * null URL, because a missing cut-out is a worse preview, not a failed sale.
   */
  async cutout(
    assetId: string,
    mode: 'background' | 'face' | 'blurBackground' | 'enhance' = 'background',
    /** Blur radius for `blurBackground`, so the preview keys on the layer's own value. */
    amount?: number,
  ): Promise<{
    url: string | null;
    /** The copy the page draws the result with; the original (`url`) prints. */
    previewUrl?: string;
    engine: string;
    cached: boolean;
    faces?: Array<{ x: number; y: number; w: number; h: number; score: number }>;
    /** Face cuts: where each head is in the cut-out, for Mini Me placement. */
    heads?: HeadMeasure[];
    /** The shop's own explanation when its daily allowance is spent. */
    notice?: string;
  }> {
    const response = await this.send(this.url('/v1/storefront/cutout'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ assetId, mode, ...(amount != null ? { amount } : {}) }),
    });
    if (!response.ok) return { url: null, engine: 'none', cached: false, ...(await budgetNotice(response)) };
    return (await response.json()) as {
      url: string | null;
      engine: string;
      cached: boolean;
      faces?: Array<{ x: number; y: number; w: number; h: number; score: number }>;
      heads?: HeadMeasure[];
    };
  }

  /** Saves the answers under a fresh id, for a share link. */
  async share(input: {
    productId: string;
    templateId: string;
    optionSetId?: string;
    values: Record<string, unknown>;
    assets: Record<string, string>;
  }): Promise<{ id: string }> {
    const response = await this.send(this.url('/v1/storefront/share'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    const body = (await response.json().catch(() => ({}))) as { id?: string; error?: string };
    if (!response.ok || !body.id) throw new Error(body.error ?? 'Deljenje trenutno nije dostupno.');
    return { id: body.id };
  }

  /** A shared design: the answers and the pictures they point at. */
  async shared(id: string): Promise<{
    templateId: string;
    values: Record<string, unknown>;
    assets?: Record<string, string>;
  }> {
    const response = await this.send(this.url(`/v1/storefront/share/${encodeURIComponent(id)}`));
    if (!response.ok) throw new Error('Podeljeni dizajn nije pronađen.');
    return (await response.json()) as { templateId: string; values: Record<string, unknown>; assets?: Record<string, string> };
  }

  /**
   * A picture painted from the shopper's words. The server builds the real
   * prompt from the option's settings; the shopper's text is only the subject.
   */
  async imagine(input: {
    productId?: string;
    templateId: string;
    optionId: string;
    text: string;
  }): Promise<{ assetId: string; url: string }> {
    const response = await this.send(this.url('/v1/storefront/imagine'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    const body = (await response.json().catch(() => ({}))) as { assetId?: string; url?: string; error?: string };
    if (!response.ok || !body.assetId || !body.url) {
      throw new Error(body.error ?? 'Crtanje trenutno nije dostupno.');
    }
    return { assetId: body.assetId, url: body.url };
  }

  /**
   * YouTube results for a song name — titles and lengths only; the search
   * itself downloads nothing. A pick then plays in YouTube's own player while
   * `prepareAudio` fetches the song's own file in the background.
   * Never throws: `code` says what went wrong, in words the card translates.
   */
  async searchYoutube(query: string): Promise<{ videos: YoutubeVideo[]; code?: string }> {
    try {
      const response = await this.send(
        this.url(`/v1/storefront/youtube/search?q=${encodeURIComponent(query)}&limit=6`),
        { headers: { accept: 'application/json' } },
      );
      const body = (await response.json().catch(() => ({}))) as { videos?: YoutubeVideo[]; code?: string };
      if (!response.ok) return { videos: [], code: body.code ?? 'unavailable' };
      return { videos: body.videos ?? [] };
    } catch {
      return { videos: [], code: 'unavailable' };
    }
  }

  /**
   * The shopper's Spotify song, found on YouTube: the best-fitting video, how
   * sure the match is, and the runners-up. Never throws, like the search.
   *
   * `prefetch`: asked ahead, while the shopper is still on the Spotify step —
   * the server then starts fetching a `high` match's song and names the fetch
   * (`prepId`), so "Da" finds it on its way or done.
   */
  async matchYoutube(
    song: { title: string; artists?: string; durationMs?: number },
    options: { prefetch?: boolean } = {},
  ): Promise<YoutubeMatch> {
    try {
      const query = [
        `title=${encodeURIComponent(song.title)}`,
        song.artists ? `artists=${encodeURIComponent(song.artists)}` : '',
        song.durationMs ? `durationMs=${Math.round(song.durationMs)}` : '',
        options.prefetch ? 'prefetch=1' : '',
      ]
        .filter(Boolean)
        .join('&');
      const response = await this.send(this.url(`/v1/storefront/youtube/match?${query}`), {
        headers: { accept: 'application/json' },
      });
      const body = (await response.json().catch(() => ({}))) as Partial<YoutubeMatch>;
      if (!response.ok || !body.video) return { candidates: [], code: body.code ?? 'unavailable' };
      const confidence = body.confidence === 'high' ? 'high' : 'low';
      return {
        video: body.video,
        confidence,
        candidates: body.candidates ?? [body.video],
        query: body.query,
        ...(confidence === 'high' && typeof body.prepId === 'string' && body.prepId ? { prepId: body.prepId } : {}),
      };
    } catch {
      return { candidates: [], code: 'unavailable' };
    }
  }

  /**
   * Whether a YouTube video plays on a shop page; a blocked one comes back with
   * playable versions of the same song. An unanswered check reads as playable
   * (the player still reports a refusal itself).
   */
  async checkYoutube(
    id: string,
    title?: string,
  ): Promise<{ embeddable: boolean; title?: string; video?: YoutubeVideo; videos: YoutubeVideo[] }> {
    try {
      const query = `id=${encodeURIComponent(id)}${title ? `&title=${encodeURIComponent(title)}` : ''}`;
      const response = await this.send(this.url(`/v1/storefront/youtube/check?${query}`), {
        headers: { accept: 'application/json' },
      });
      if (!response.ok) return { embeddable: true, videos: [] };
      const body = (await response.json().catch(() => ({}))) as {
        embeddable?: boolean;
        title?: string;
        /** The blocked video itself — its length lets the shopper cut it unheard. */
        video?: YoutubeVideo;
        videos?: YoutubeVideo[];
      };
      return {
        embeddable: body.embeddable !== false,
        title: body.title,
        video: body.video && body.video.id === id ? body.video : undefined,
        videos: body.videos ?? [],
      };
    } catch {
      return { embeddable: true, videos: [] };
    }
  }

  /** Asks the server to fetch a song (by link or search) for the cutter. */
  async prepareAudio(input: string): Promise<{ id: string }> {
    const response = await this.send(this.url('/v1/storefront/audio/prepare'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ input }),
    });
    const body = (await response.json().catch(() => ({}))) as { id?: string; error?: string };
    if (!response.ok || !body.id) {
      throw new Error(body.error ?? 'The song could not be fetched right now.');
    }
    return { id: body.id };
  }

  async prepareStatus(id: string): Promise<AudioPrepStatus> {
    const response = await this.send(this.url(`/v1/storefront/audio/prepare/${id}`), {
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('The song could not be fetched right now.');
    return (await response.json()) as AudioPrepStatus;
  }

  /**
   * One order, opened for Studio's order editor: the order's own release as a
   * definition and its current answers, sounds and pictures. Session-
   * authenticated, like the preview — the frame is served from the API's own
   * origin, so the Studio cookie rides along.
   */
  async loadOrderEdit(personalizationId: string): Promise<{
    definition: ProductDefinition;
    restore: {
      values: Values;
      audios: AudioSelection[];
      assets: Array<{ id: string; url: string }>;
      collageThumbs?: Record<string, string>;
      collageFramers?: Record<string, string>;
    };
    revision: number;
    variation: { id: number | null; attributes: Record<string, string> } | null;
  }> {
    const response = await this.send(
      this.url(this.externalRevisionToken?'/v1/external/revision/customer/edit':`/v1/admin/personalizations/${encodeURIComponent(personalizationId)}/edit`),
      { headers: { accept: 'application/json',...(this.externalRevisionToken?{'x-pz-revision-link':this.externalRevisionToken}:{}) }, credentials: 'same-origin', cache: 'no-store' },
    );
    if (!response.ok) {
      const detail = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(
        detail.error ??
          (response.status === 401
            ? 'Sign in to the studio to edit this order.'
            : `Could not open the order (${response.status}).`),
      );
    }
    const body = (await response.json()) as {
      definition: ProductDefinition;
      restore: { values: Values; audios: AudioSelection[]; assets: Array<{ id: string; url: string }> };
      revision: number;
      variation: { id: number | null; attributes: Record<string, string> } | null;
    };
    return { ...body, definition: localizeDefaults(body.definition) };
  }

  /** A committed personalization's answers, for the edit-from-cart flow. */
  async loadPersonalization(id: string): Promise<{
    values: Values;
    audios: AudioSelection[];
    assets: Array<{ id: string; url: string }>;
    /** Photo collages only: each photo's 256 px thumbnail, for the step's tray. */
    collageThumbs?: Record<string, string>;
    /** Photo collages only: what each photo's framer opens (the 2400 px preview copy). */
    collageFramers?: Record<string, string>;
  }> {
    const response = await this.send(this.url(`/v1/storefront/personalizations/${id}`), {
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Sačuvana personalizacija nije pronađena.');
    return (await response.json()) as {
      values: Values;
      audios: AudioSelection[];
      assets: Array<{ id: string; url: string }>;
      collageThumbs?: Record<string, string>;
      collageFramers?: Record<string, string>;
    };
  }

  /** "AI snima glasovnu": the typed text read by one of the shop's AI voices. */
  /**
   * The shopper's text read by the shop's AI voice. `takes: 2` asks for two
   * readings of the same text in the same voice, to choose between; an older
   * API answers with one, which is then the only take.
   */
  async tts(
    text: string,
    voiceId?: string,
    takes: 1 | 2 = 1,
  ): Promise<{ storageKey: string; url: string; takes: Array<{ storageKey: string; url: string }> }> {
    const response = await this.send(this.url('/v1/storefront/tts'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text, voiceId, takes }),
    });
    const body = (await response.json().catch(() => ({}))) as {
      storageKey?: string;
      url?: string;
      takes?: Array<{ storageKey?: string; url?: string }>;
      error?: string;
    };
    if (!response.ok || !body.url) {
      throw new Error(body.error ?? 'Generisanje glasa nije uspelo — pokušaj ponovo.');
    }
    const list = (body.takes ?? [])
      .filter((take): take is { storageKey: string; url: string } => Boolean(take.url && take.storageKey));
    return {
      storageKey: body.storageKey!,
      url: body.url,
      takes: list.length > 0 ? list : [{ storageKey: body.storageKey!, url: body.url }],
    };
  }

  /** Starts an AI song; the returned id is polled with songStatus. */
  async songStart(input: {
    style: string;
    lyrics: string;
    voiceGender: string;
  }): Promise<{ id: string }> {
    const response = await this.send(this.url('/v1/storefront/song'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    const body = (await response.json().catch(() => ({}))) as { id?: string; error?: string };
    if (!response.ok || !body.id) {
      throw new Error(body.error ?? 'Pravljenje pesme nije moglo da počne — pokušaj ponovo.');
    }
    return { id: body.id };
  }

  async songStatus(id: string): Promise<{
    status: string;
    songs: Array<{ id: string; title: string; url: string; storageKey?: string; durationMs?: number }>;
    message?: string;
    /** The stage the provider reported: the lyrics set to a melody, then the first version. */
    phase?: 'lyrics' | 'first' | null;
  }> {
    const response = await this.send(this.url(`/v1/storefront/song/${id}`), {
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Provera statusa pesme nije uspela.');
    return (await response.json()) as {
      status: string;
      songs: Array<{ id: string; title: string; url: string; storageKey?: string; durationMs?: number }>;
      message?: string;
    };
  }

  /** AI-written lyrics from a plain description of the occasion. */
  async songLyrics(description: string, style?: string): Promise<string> {
    const response = await this.send(this.url('/v1/storefront/song/lyrics'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ description, style }),
    });
    const body = (await response.json().catch(() => ({}))) as { lyrics?: string; error?: string };
    if (!response.ok || !body.lyrics) {
      throw new Error(body.error ?? 'AI pisanje teksta nije uspelo — pokušaj ponovo.');
    }
    return body.lyrics;
  }

  async commit(input: {
    cartPreviewAssetId?: string;
    releaseId?: string;
    /** Omitted when previewing: there is no product row to attach to. */
    productId?: string;
    templateId: string;
    optionSetId: string;
    /** Every set the product asked, its own first — validation and pricing see all of them. */
    optionSetIds?: string[];
    values: Values;
    audios?: AudioSelection[];
    variation?: { id: number | null; attributes: Record<string, string> };
    /** 'studio' marks a test made from the preview, so it is not a real sale. */
    source?: 'web' | 'studio';
    /**
     * This form session's key. A retry after a dropped response replays the
     * first result instead of adding a second personalization to the order.
     */
    idempotencyKey?: string;
    /** The shopper ticked "I checked the design" on a product that asks for it. */
    proofApproved?: boolean;
    /**
     * Saved ahead of the click (see cart-prep.ts): the server keeps it held —
     * no render, no sound download — until `release` or the cart claims it.
     */
    hold?: boolean;
    /** The previous held design of this page, replaced by this one (see main.ts heldId). */
    supersedes?: string;
  }): Promise<{ id: string; playerUrl: string | null; priceDelta: number; extraProducts?: Array<{ wooProductId: number; quantity: number }> }> {
    const response = await this.send(this.url(this.externalOrderToken ? '/v1/external/customer/commit' : '/v1/storefront/personalizations'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(this.externalOrderToken ? {'x-pz-order-link':this.externalOrderToken} : {}) },
      body: JSON.stringify(input),
    });
    const body = (await response.json().catch(() => ({}))) as {
      id?: string;
      playerUrl?: string | null;
      priceDelta?: number;
      extraProducts?: Array<{ wooProductId: number; quantity: number }>;
      error?: string;
      missing?: string[];
      invalid?: Array<{ label?: string; reason?: string; code?: string; detail?: string }>;
      maxSeconds?: number;
      estimatedSeconds?: number;
    };
    if (!response.ok) {
      // The one refusal a shopper can act on themselves: the sounds they
      // arranged are longer than the module holds. Said in their language, with
      // both numbers, because "shorten it" without "by how much" is useless.
      if (body.maxSeconds) {
        const clock = (seconds: number): string =>
          `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
        throw new Error(
          `Zvuk je predugačak: ${clock(body.estimatedSeconds ?? 0)} od najviše ` +
            `${clock(body.maxSeconds)}. Skrati neki deo ili smanji pauze.`,
        );
      }
      if (body.missing?.length) {
        throw new Error(`Popuni: ${body.missing.join(', ')}`);
      }
      if (body.invalid?.length) {
        // The server names the answer and why, in English for its logs; the
        // shopper reads the same thing in Serbian ("Naziv pesme: najviše 30 znakova").
        const reasons = body.invalid
          .map((i) => [i.label, collageCommitReason(i.code, i.detail) ?? (i.reason ? commitReason(i.reason) : '')].filter(Boolean).join(': '))
          .filter(Boolean);
        throw new Error(reasons.length ? reasons.join('; ') : COMMIT_FAILED);
      }
      if ((body as { code?: string }).code === 'release_changed') {
        throw new Error('Dizajn je upravo ažuriran. Osveži stranicu i pokušaj ponovo.');
      }
      // Anything else is ours to fix, not the shopper's to read in English.
      throw new Error(COMMIT_FAILED);
    }
    return {
      id: body.id!,
      playerUrl: body.playerUrl ?? null,
      priceDelta: body.priceDelta ?? 0,
      // Bundled products ("add product" choices) ride along to the cart; the
      // connector adds them beside the personalized line.
      extraProducts: body.extraProducts ?? [],
    };
  }

  /**
   * A design saved ahead of the click goes into the cart now: its render and
   * sounds may start. Sent as a beacon — it has to survive the page
   * navigating away, carries nothing, and needs no answer — and repeated on
   * the server by the connector's own read, so losing it changes nothing.
   */
  release(personalizationId: string): void {
    if(this.externalOrderToken)return; // The final native-line confirmation releases its jobs atomically.
    const url = this.url(`/v1/storefront/personalizations/${encodeURIComponent(personalizationId)}/release`);
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(url)) return;
    } catch {
      /* fall through to fetch */
    }
    try {
      void fetch(url, { method: 'POST', keepalive: true, mode: 'no-cors' }).catch(() => undefined);
    } catch {
      /* the connector releases it at add-to-cart anyway */
    }
  }
}
